import { useMemo } from 'react';
import * as R from 'remeda';
import { Chip, Skeleton, SvgIcon, Tooltip } from '@rossum/ui/material';
import { borderRadius } from '@rossum/ui/theme';
import { useComponentsContext } from '../ComponentsContext';
import { TileChip as TileChipType } from './types';

const SKELETON_MIN_WIDTH = 40;
const SKELETON_MAX_WIDTH = 60;
const CHIP_HEIGHT = 24;
const CHIP_MAX_WIDTH = '35ch';

type TileChipProps = {
  chip: TileChipType;
};

export const TileChip = ({ chip }: TileChipProps) => {
  const { LinkComponent } = useComponentsContext();
  const skeletonWidth = useMemo(
    () => R.randomInteger(SKELETON_MIN_WIDTH, SKELETON_MAX_WIDTH),
    []
  );

  if (chip.isLoading) {
    return (
      <Skeleton
        variant="rounded"
        width={skeletonWidth}
        height={CHIP_HEIGHT}
        sx={{ borderRadius: borderRadius('l') }}
      />
    );
  }

  const { Icon, to, color, label } = chip;

  const chipElement = (
    <Chip
      label={label}
      size="small"
      color={color}
      icon={
        Icon ? (
          <SvgIcon fontSize="small">
            <Icon />
          </SvgIcon>
        ) : undefined
      }
      slotProps={
        label === ''
          ? {
              // workaround to remove left padding when there is no label, as MUI doesn't support it out of the box
              label: {
                sx: {
                  paddingLeft: 0,
                  paddingRight: 1.5,
                },
              },
            }
          : {
              label: {
                sx: {
                  maxWidth: CHIP_MAX_WIDTH,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                },
              },
            }
      }
      component={to ? LinkComponent : 'div'}
      to={to}
      clickable={!!to}
      data-cy={chip['data-cy']}
    />
  );

  if (chip.tooltip) {
    return <Tooltip title={chip.tooltip}>{chipElement}</Tooltip>;
  }

  return chipElement;
};
